import {
  forwardRef,
  useState,
  useEffect,
  type CSSProperties,
  type ReactNode,
  type RefObject,
  type MouseEvent,
  type ForwardedRef,
} from 'react';
import { useGesture } from '@use-gesture/react';
import clsx from 'clsx';
import { useMeasure } from 'react-use';
import { ModalContent } from '../Modal/ModalContent/ModalContent';
import { ModalOverlay } from '../Modal/ModalOverlay/ModalOverlay';
import { useModalTransition } from '../Modal/useModalTransition';
import { useModalSheetInputIosKeyboard } from '../useModalSheetInputIosKeyboard';
import styles from './Sheet.module.css';

function getHeightViewModel(
  params: Pick<Props, 'height' | 'contentWrapperClassName'>
): {
  containerClassName: string;
  contentWrapperClassName: string;
  bodyClassName: string;
} {
  const {
    height: variant,
    contentWrapperClassName: contentWrapperClassNameProp,
  } = params;
  switch (variant) {
    case 'full':
      return {
        containerClassName: styles['container-full-screen'],
        contentWrapperClassName: styles['content-wrapper-full-screen'],
        bodyClassName: clsx(
          styles['body-full-screen'],
          contentWrapperClassNameProp
        ),
      };
    case 'auto':
    case undefined:
      return {
        containerClassName: styles.container,
        contentWrapperClassName: styles['content-wrapper'],
        bodyClassName: clsx(styles.body, contentWrapperClassNameProp),
      };
  }
}

type Props = {
  active: boolean;
  testid?: string;
  children: ReactNode;
  ariaLabel: string;
  initialFocusRef: RefObject<HTMLElement> | undefined;
  onRequestDismiss: () => void;
  onMouseDown?: (e: MouseEvent) => void;
  height?: 'auto' | 'full';
  contentWrapperClassName?: string;
  className?: string;
  contentClassName?: string;
  contentBodyClassName?: string;
  swipeCloseSegmentEvent?: () => void;
  touchBarClassName?: string;
  showTouchBar?: boolean;
};

export const Sheet = forwardRef(
  (props: Props, forwardedRef: ForwardedRef<HTMLDivElement>) => {
    const {
      active,
      ariaLabel,
      onRequestDismiss,
      initialFocusRef,
      testid,
      onMouseDown,
      className,
      contentClassName,
      contentBodyClassName,
      swipeCloseSegmentEvent,
      touchBarClassName,
      showTouchBar = true,
    } = props;
    const [measureRef, { height }] = useMeasure<HTMLDivElement>();
    const [dragY, setDragY] = useState(0);
    const [entering, setEntering] = useState(true);

    const { shouldRender, closing, handleTransitionEnd } = useModalTransition({
      isOpen: active,
    });

    useEffect(() => {
      if (!shouldRender) {
        setEntering(true);
        return;
      }
      const raf = requestAnimationFrame(() => {
        setEntering(false);
      });
      return () => cancelAnimationFrame(raf);
    }, [shouldRender]);

    const { containerClassName, bodyClassName, contentWrapperClassName } =
      getHeightViewModel(props);

    const dragPct = height === 0 ? 0 : dragY / height;

    const drag = useGesture({
      onDrag(state) {
        setDragY(state.xy[1] - state.initial[1]);
      },
      onDragEnd(state) {
        const { direction } = state;
        if (direction[1] === 1) {
          onRequestDismiss();

          if (swipeCloseSegmentEvent) {
            swipeCloseSegmentEvent();
          }
        }
        setDragY(0);
      },
    });

    const { overlayRef } = useModalSheetInputIosKeyboard();

    if (!shouldRender) {
      return null;
    }

    const contentStyle: CSSProperties =
      dragY !== 0
        ? {
            transform: `translate3d(0, ${dragPct * 100}%, 0)`,
            transition: 'none',
          }
        : {};

    return (
      <ModalOverlay
        ref={(element) => {
          if (typeof forwardedRef === 'object' && forwardedRef !== null) {
            forwardedRef.current = element;
          }
          overlayRef.current = element;
        }}
        onDismiss={onRequestDismiss}
        data-testid={testid}
        className={clsx(containerClassName, className, {
          [styles['overlay-entering']]: entering,
          [styles.closing]: closing,
        })}
        isOpen={shouldRender}
        initialFocusRef={initialFocusRef}
        onTransitionEnd={handleTransitionEnd}
      >
        <ModalContent
          ariaLabel={ariaLabel}
          data-testid={`${testid}-body`}
          style={contentStyle}
          ref={measureRef}
          className={clsx(contentWrapperClassName, contentClassName, {
            [styles['content-entering']]: entering,
            [styles['content-closing']]: closing,
          })}
          onMouseDown={(e) => {
            onMouseDown ? onMouseDown(e) : null;
          }}
        >
          <div
            data-testid={`${testid}-body-wrapper`}
            className={clsx(bodyClassName, contentBodyClassName)}
          >
            {showTouchBar ? (
              <span
                data-testid={`${testid}-touch-bar`}
                className={
                  touchBarClassName ? touchBarClassName : styles['touch-bar']
                }
                {...drag()}
              ></span>
            ) : null}
            {props.children}
          </div>
        </ModalContent>
      </ModalOverlay>
    );
  }
);
